import { Icon } from '@clientbase/clientbase-library'
import { Box, Divider, Stack, Typography } from '@mui/material'

import { START_OFFER_PRICE } from 'components/Modal/ModalNewPlansSmall/utils/smallPlanContent'
import { NEW_PLANS_DATA } from 'components/PlanItem/NewPlanCard'
import { statusChipBase } from 'components/Status/Status.styles'

import { SaasPlanKey } from 'utils/saas-checkout-event'

import {
  SaasPlanOption,
  SaasSetupOption,
  SaasSubscription
} from '../SaasCheckout.types'
import SetupAddon from './SetupAddon'

export type BillingCycle = 'monthly' | 'annual'

type PlanStepProps = {
  plan: string
  planName: string
  planKey: SaasPlanKey
  planOption?: SaasPlanOption
  subscription: SaasSubscription | null
  cycle: BillingCycle
  onCycleChange: (cycle: BillingCycle) => void
  /** Implantação disponível para o plano e a forma de cobrança escolhidos. */
  setupOption?: SaasSetupOption | null
  withSetup: boolean
  onSetupChange: (selected: boolean) => void
}

const formatMoney = (value?: number | null) =>
  (value ?? 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })

const formatDate = (value?: string | null) =>
  value ? new Date(`${value}T12:00:00`).toLocaleDateString('pt-BR') : ''

const FEATURES_SHOWN = 5

// Uma opção de cobrança (mensal/anual), clicável como um radio.
const CycleOption = ({
  selected,
  title,
  price,
  detail,
  badge,
  onClick
}: {
  selected: boolean
  title: string
  price: string
  detail: string
  badge?: string
  onClick: () => void
}) => (
  <Box
    component="button"
    type="button"
    role="radio"
    aria-checked={selected}
    onClick={onClick}
    sx={{
      flex: 1,
      display: 'flex',
      alignItems: 'flex-start',
      textAlign: 'left',
      cursor: 'pointer',
      font: 'inherit',
      color: 'inherit',
      padding: '14px 16px',
      borderRadius: '8px',
      border: '1px solid',
      borderColor: selected ? 'primary.main' : '#E3E6E3',
      boxShadow: selected ? '0 0 0 1px #01F7C2 inset' : 'none',
      backgroundColor: selected ? '#E6FDEA' : '#FFFFFF',
      transition: 'border-color 0.15s ease, background-color 0.15s ease',
      '&:hover': { borderColor: selected ? 'primary.main' : '#BEC0BE' },
      '&:focus-visible': { outline: '2px solid #366AB2', outlineOffset: 2 }
    }}
  >
    <Stack gap={0.5} width="100%">
      <Stack
        minHeight="26px"
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        gap={1}
      >
        <Typography variant="smBold" component="span">
          {title}
        </Typography>
        {badge && (
          <Box
            component="span"
            sx={{
              ...statusChipBase,
              color: '#1A803C',
              backgroundColor: '#F2FFF6'
            }}
          >
            {badge}
          </Box>
        )}
      </Stack>
      <Typography variant="mdBold" component="span" color="#231F20">
        {price}
      </Typography>
      <Typography variant="smLight" component="span" color="#646664">
        {detail}
      </Typography>
    </Stack>
  </Box>
)

// Linha do resumo: rótulo à esquerda, valor à direita.
const SummaryRow = ({
  label,
  value,
  strong = false
}: {
  label: string
  value: string
  strong?: boolean
}) => (
  <Stack
    direction="row"
    justifyContent="space-between"
    alignItems="baseline"
    gap={2}
  >
    <Typography variant="smLight" component="span" color="#646664">
      {label}
    </Typography>
    <Typography
      variant={strong ? 'mdBold' : 'smBold'}
      component="span"
      textAlign="right"
      color="#231F20"
    >
      {value}
    </Typography>
  </Stack>
)

const PlanStep = ({
  plan,
  planName,
  planKey,
  planOption,
  subscription,
  cycle,
  onCycleChange,
  setupOption,
  withSetup,
  onSetupChange
}: PlanStepProps) => {
  const data = NEW_PLANS_DATA[plan]
  const monthly = planOption?.monthly_amount ?? data?.price ?? 0
  const annual = planOption?.annual
  const isOffer = planKey === 'start_offer'
  const isUpgrade = subscription?.status === 'active'
  const upgrade = planOption?.upgrade
  const shared = subscription?.shared

  const planToday = (() => {
    if (isUpgrade) return upgrade?.amount ?? 0
    if (isOffer) return START_OFFER_PRICE
    return cycle === 'annual' && annual ? annual.amount : monthly
  })()
  const setupToday = withSetup && setupOption ? setupOption.amount : 0
  const chargeToday = planToday + setupToday
  const isAnnual = cycle === 'annual' && !!annual

  return (
    <Stack gap={3}>
      <Stack gap={0.5}>
        <Typography variant="smLight" component="span" color="#646664">
          Plano escolhido
        </Typography>
        <Typography variant="lgBold" component="h3" color="#231F20" m={0}>
          Plano {planName}
        </Typography>
        {data?.subtitle && (
          <Typography variant="smLight" component="p" color="#646664" m={0}>
            {data.subtitle}
          </Typography>
        )}
      </Stack>

      {!isUpgrade && !isOffer && annual && (
        <Stack gap={1.5}>
          <Typography variant="smBold" component="span">
            Como você quer pagar?
          </Typography>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            gap={1.5}
            role="radiogroup"
            aria-label="Forma de cobrança"
          >
            <CycleOption
              selected={cycle === 'monthly'}
              title="Mensal"
              price={`${formatMoney(monthly)}/mês`}
              detail="Cobrado todo mês"
              onClick={() => onCycleChange('monthly')}
            />
            {annual && (
              <CycleOption
                selected={cycle === 'annual'}
                title="Anual"
                price={`${formatMoney(annual.monthly_equivalent)}/mês`}
                detail={`${formatMoney(
                  annual.amount
                )} cobrados uma vez por ano`}
                badge={`${annual.discount_percent}% off`}
                onClick={() => onCycleChange('annual')}
              />
            )}
          </Stack>
        </Stack>
      )}

      {!isUpgrade && setupOption && (
        <SetupAddon
          amount={setupOption.amount}
          multiplier={setupOption.multiplier}
          selected={withSetup}
          onToggle={onSetupChange}
        />
      )}

      {data?.features?.length > 0 && (
        <Stack gap={1}>
          <Typography variant="smBold" component="span">
            Incluso no plano
          </Typography>
          <Stack
            component="ul"
            gap={0.75}
            m={0}
            p={0}
            sx={{ listStyle: 'none' }}
          >
            {data.features.slice(0, FEATURES_SHOWN).map((feature) => (
              <Stack
                component="li"
                key={feature}
                direction="row"
                gap={1}
                alignItems="flex-start"
              >
                <Icon icon="check" size={2} colorSX="#3D892D" />
                <Typography variant="smLight" component="span">
                  {feature}
                </Typography>
              </Stack>
            ))}
            {data.features.length > FEATURES_SHOWN && (
              <Typography
                variant="smLight"
                component="li"
                color="#646664"
                pl={3}
              >
                e mais {data.features.length - FEATURES_SHOWN} recursos
              </Typography>
            )}
          </Stack>
        </Stack>
      )}

      <Stack gap={1.25} p={2} borderRadius="8px" bgcolor="#F2F5F2">
        {setupToday > 0 && (
          <>
            <SummaryRow
              label={
                isAnnual
                  ? `Plano ${planName}, anuidade`
                  : `Plano ${planName}, 1ª mensalidade`
              }
              value={formatMoney(planToday)}
            />
            <SummaryRow
              label="Implantação, pagamento único"
              value={formatMoney(setupToday)}
            />
            <Divider sx={{ borderColor: '#E3E6E3' }} />
          </>
        )}
        <SummaryRow
          label={isUpgrade ? 'Diferença proporcional, hoje' : 'Cobrança hoje'}
          value={formatMoney(chargeToday)}
          strong
        />
        <Divider sx={{ borderColor: '#E3E6E3' }} />
        {setupToday > 0 && (
          <SummaryRow
            label="Depois"
            value={
              isAnnual
                ? `${formatMoney(planToday)} por ano`
                : `${formatMoney(planToday)} por mês`
            }
          />
        )}
        {isUpgrade && (
          <SummaryRow
            label={
              upgrade?.next_due_date
                ? `A partir de ${formatDate(upgrade.next_due_date)}`
                : 'Depois'
            }
            value={`${formatMoney(monthly)} por mês`}
          />
        )}
        <SummaryRow
          label="Formas de pagamento"
          value="PIX ou cartão de crédito"
        />
      </Stack>

      {isUpgrade && upgrade && (
        <Typography variant="smLight" component="p" color="#646664" m={0}>
          {upgrade.amount > 0
            ? `O valor de hoje cobre os ${upgrade.days_remaining} dias até o próximo vencimento. O plano novo é liberado assim que o pagamento é confirmado.`
            : 'O plano novo é liberado na hora, sem cobrança agora.'}
        </Typography>
      )}

      {shared && (
        <Typography variant="smLight" component="p" color="#646664" m={0}>
          Esta conta é cobrada na assinatura de{' '}
          <strong>{shared.payer_name}</strong>, junto com outras contas. O valor
          novo entra na fatura consolidada.
        </Typography>
      )}

      {!isUpgrade && (
        <Stack
          direction="row"
          alignItems="center"
          gap={1}
          px={2}
          py={1.5}
          borderRadius="8px"
          bgcolor="#F2FFF6"
          border="1px solid #CDEBD6"
        >
          <Icon icon="checkCircle" size={2} colorSX="#1A803C" />
          <Typography variant="smBold" component="p" m={0} color="#1A803C">
            O plano é ativado assim que o pagamento é confirmado.
          </Typography>
        </Stack>
      )}
    </Stack>
  )
}

export default PlanStep
